import { getLocalTimeZone, today } from "@internationalized/date";
import { t } from "@travily/i18n";
import { Icon } from "@ui/components/Icon/Icon";
import { optionTint } from "@ui/components/internal/OptionTint";
import { ScrollArea } from "@ui/components/ScrollArea";
import { tv } from "@ui/lib/tv";
import { OverlayTriggerStateContext } from "@ui/primitives/Popover";
import { type DateValue, useLocale } from "@ui/primitives/shared";
import { lazy, Suspense, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  WHEN_DURATION_COLORS,
  WHEN_DURATION_I18N_KEYS,
  WHEN_DURATION_PRESETS,
  type WhenColor,
  type WhenDurationPreset,
  type WhenSelection,
  whenDurationIcon,
  whenMonthColor,
  whenMonthIcon,
} from "../types";

// Lazy-load the date field so its React Aria portal mounts AFTER the
// outer popover Dialog has finished attaching to the DOM. This dodges a
// Storybook @storybook/test focus-instrumentation crash that fires when
// CalendarCell.focus() runs against a node still in the process of being
// portalled into a parent that itself just mounted.
const WhenDateField = lazy(() =>
  import("./WhenDateField").then((m) => ({
    default: m.WhenDateField,
  })),
);

const whenPopover = tv({
  slots: {
    root: "flex min-h-0 flex-col",
    tabs: "flex shrink-0 items-center gap-2.5 border-b border-neutral-100 p-5 pb-3",
    tab: [
      "cursor-pointer text-2xl font-extrabold tracking-2xl leading-8 outline-none transition-colors",
      "rounded focus-visible:ring-2 focus-visible:ring-neutral-300",
    ],
    tabInactive: "text-neutral-200",
    tabActive: "text-neutral-900",
    tabDivider: "h-6 w-px bg-neutral-100",
    // Padding lives on the consumer (popover body / accordion section) so the
    // popover wraps the active panel in p-5.
    // A flex column, not a block: the popover card caps its height on short
    // viewports, and only a flex chain from the card down to the list lets the
    // list yield instead of the card clipping the date field off the bottom.
    body: "flex min-h-0 flex-col p-5",
    // The panel pairs a scrollable quick-pick list with a custom date field.
    // Padding-neutral and reused on desktop (inside the When popover tabs) and
    // on mobile (as a stacked accordion section).
    panel: "flex min-h-0 flex-col gap-4",
    listGroup: "flex min-h-0 flex-col gap-3",
    hint: "text-sm leading-5 text-neutral-600",
    // 6 rows × 40px + 5 gaps × 4px + half a row (22px) = 282px. The half-cut row
    // is the point: 12 months in a 6-row window otherwise reads as the whole list.
    // Bleeding to the card edge puts the rail where the design has it; the
    // viewport pads the rows back to the popover body's inset.
    list: "-mx-5 max-h-[282px]",
    listViewport: "flex flex-col gap-1 px-5 scroll-py-1",
    item: "flex w-full cursor-pointer items-center gap-3 rounded-5xl py-1 pl-2 pr-3 text-left outline-none",
    itemIcon: "flex size-8 shrink-0 items-center justify-center [&>svg]:size-6",
    itemLabel: "min-w-0 flex-1 truncate text-base font-semibold tracking-[-0.16px] text-neutral-600",
    itemLabelActive: "text-neutral-900",
    // Hide the inner DatePickerLabel — we pass `label=""` for accessible names
    // via aria-label, but the empty label span still occupies a line-height of
    // space without this rule (it's a <span> not a <label> in RAC's compound).
    picker: "flex shrink-0 flex-col border-t border-neutral-100 pt-4 [&_span:empty]:hidden",
  },
});

// Per-color hover/select tint: the shared optionTint single-select recipe (idle
// rows whisper their colour at -25 on hover; the selected row sits at -50,
// matching its icon color, and deepens to -100 on focus).
const itemColorClass = (color: WhenColor, active: boolean) => optionTint({ color, isSelected: active });

const NUM_FUTURE_MONTHS = 12;

interface MonthOption {
  year: number;
  month: number;
  label: string;
}

function buildMonthOptions(locale: string): MonthOption[] {
  const todayDate = today(getLocalTimeZone());
  const formatter = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" });
  const options: MonthOption[] = [];
  for (let i = 0; i < NUM_FUTURE_MONTHS; i++) {
    const monthIndex = todayDate.month - 1 + i;
    const year = todayDate.year + Math.floor(monthIndex / 12);
    const month = (monthIndex % 12) + 1;
    const label = formatter.format(new Date(year, month - 1, 1));
    options.push({ year, month, label });
  }
  return options;
}

export interface WhenPanelProps {
  value: WhenSelection;
  onChange: (value: WhenSelection) => void;
}

/**
 * Start section body: a quick-pick list of the next 12 months plus a custom
 * start-date field. Shared by the desktop When popover (Start tab) and the
 * mobile stacked search (Start date section).
 */
export function WhenStartPanel({ value, onChange }: WhenPanelProps) {
  const styles = whenPopover();
  const { locale } = useLocale();
  const monthOptions = useMemo(() => buildMonthOptions(locale), [locale]);

  const activeStartMonth = value.start?.kind === "month" ? `${value.start.year}-${value.start.month}` : null;
  const startDateValue = value.start?.kind === "date" ? value.start.value : null;

  const handleStartMonth = (option: MonthOption) => {
    onChange({ ...value, start: { kind: "month", year: option.year, month: option.month } });
  };

  const handleStartDate = (date: DateValue | null) => {
    onChange({ ...value, start: date ? { kind: "date", value: date } : null });
  };

  return (
    <div className={styles.panel()}>
      <div className={styles.listGroup()}>
        <p className={styles.hint()}>{t("components.search_bar.when_start_hint")}</p>
        <ScrollArea className={styles.list()} render={<ul />} viewportClassName={styles.listViewport()}>
          {monthOptions.map((option) => {
            const id = `${option.year}-${option.month}`;
            const isActive = id === activeStartMonth;
            const color = whenMonthColor(option.month);
            return (
              <li key={id}>
                <button
                  type="button"
                  aria-pressed={isActive}
                  className={`${styles.item()} ${itemColorClass(color, isActive)}`}
                  onClick={() => handleStartMonth(option)}
                >
                  <span aria-hidden="true" className={styles.itemIcon()}>
                    <Icon name={whenMonthIcon(option.month)} shadow="layered" type="sticker" />
                  </span>
                  <span className={`${styles.itemLabel()}${isActive ? ` ${styles.itemLabelActive()}` : ""}`}>
                    {option.label}
                  </span>
                </button>
              </li>
            );
          })}
        </ScrollArea>
      </div>
      <div className={styles.picker()}>
        <Suspense fallback={<div aria-hidden="true" className="h-10 w-full rounded-xl bg-neutral-50" />}>
          <WhenDateField
            aria-label={t("components.search_bar.when_start_placeholder")}
            label=""
            leadIcon={<Icon name="date" type="sticker" />}
            minValue={today(getLocalTimeZone())}
            onChange={handleStartDate}
            placeholder={t("components.search_bar.when_start_placeholder")}
            value={startDateValue}
          />
        </Suspense>
      </div>
    </div>
  );
}

/**
 * End section body: trip-length duration presets plus a custom end-date field.
 * Shared by the desktop When popover (End tab) and the mobile stacked search
 * (End date section).
 */
export function WhenEndPanel({ value, onChange }: WhenPanelProps) {
  const styles = whenPopover();
  // Picking an end completes the window, so dismiss the popover (mirrors
  // WherePopover). The mobile stacked search shadows this with a no-op overlay
  // state, so the accordion stays put there.
  const overlayState = useContext(OverlayTriggerStateContext);

  const activeEndDuration = value.end?.kind === "duration" ? value.end.preset : null;
  const endDateValue = value.end?.kind === "date" ? value.end.value : null;

  const handleEndDuration = (preset: WhenDurationPreset) => {
    onChange({ ...value, end: { kind: "duration", preset } });
    overlayState?.close();
  };

  const handleEndDate = (date: DateValue | null) => {
    onChange({ ...value, end: date ? { kind: "date", value: date } : null });
    if (date) overlayState?.close();
  };

  return (
    <div className={styles.panel()}>
      <div className={styles.listGroup()}>
        <p className={styles.hint()}>{t("components.search_bar.when_end_hint")}</p>
        <ScrollArea className={styles.list()} render={<ul />} viewportClassName={styles.listViewport()}>
          {WHEN_DURATION_PRESETS.map((preset) => {
            const isActive = preset === activeEndDuration;
            const color = WHEN_DURATION_COLORS[preset];
            return (
              <li key={preset}>
                <button
                  type="button"
                  aria-pressed={isActive}
                  className={`${styles.item()} ${itemColorClass(color, isActive)}`}
                  onClick={() => handleEndDuration(preset)}
                >
                  <span aria-hidden="true" className={styles.itemIcon()}>
                    <Icon name={whenDurationIcon(color)} shadow="layered" type="sticker" />
                  </span>
                  <span className={`${styles.itemLabel()}${isActive ? ` ${styles.itemLabelActive()}` : ""}`}>
                    {t(WHEN_DURATION_I18N_KEYS[preset])}
                  </span>
                </button>
              </li>
            );
          })}
        </ScrollArea>
      </div>
      <div className={styles.picker()}>
        <Suspense fallback={<div aria-hidden="true" className="h-10 w-full rounded-xl bg-neutral-50" />}>
          <WhenDateField
            aria-label={t("components.search_bar.when_end_placeholder")}
            label=""
            leadIcon={<Icon name="date" type="sticker" />}
            minValue={today(getLocalTimeZone())}
            onChange={handleEndDate}
            placeholder={t("components.search_bar.when_end_placeholder")}
            value={endDateValue}
          />
        </Suspense>
      </div>
    </div>
  );
}

export interface WhenPopoverProps {
  value: WhenSelection;
  onChange: (value: WhenSelection) => void;
}

export function WhenPopover({ value, onChange }: WhenPopoverProps) {
  const styles = whenPopover();
  const [activeView, setActiveView] = useState<"start" | "end">(value.start ? "end" : "start");

  // Auto-advance to End the FIRST time start is set. Tracks the previous value
  // so that if the user manually clicks back to Start (with start already set),
  // we don't immediately bounce them to End again.
  const prevStartRef = useRef<WhenSelection["start"]>(value.start);
  useEffect(() => {
    const wasEmpty = !prevStartRef.current;
    const isNowSet = !!value.start;
    if (wasEmpty && isNowSet && activeView === "start") {
      setActiveView("end");
    }
    prevStartRef.current = value.start;
  }, [value.start, activeView]);

  return (
    <div className={styles.root()}>
      <div className={styles.tabs()} role="tablist" aria-label={t("components.search_bar.when_title")}>
        <button
          type="button"
          role="tab"
          aria-selected={activeView === "start"}
          className={`${styles.tab()} ${activeView === "start" ? styles.tabActive() : styles.tabInactive()}`}
          onClick={() => setActiveView("start")}
        >
          {t("components.search_bar.when_start_title")}
        </button>
        <span aria-hidden="true" className={styles.tabDivider()} />
        <button
          type="button"
          role="tab"
          aria-selected={activeView === "end"}
          className={`${styles.tab()} ${activeView === "end" ? styles.tabActive() : styles.tabInactive()}`}
          onClick={() => setActiveView("end")}
        >
          {t("components.search_bar.when_end_title")}
        </button>
      </div>

      <div className={styles.body()}>
        {activeView === "start" ? (
          <WhenStartPanel onChange={onChange} value={value} />
        ) : (
          <WhenEndPanel onChange={onChange} value={value} />
        )}
      </div>
    </div>
  );
}
